// Core Libraries
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pagination } from "antd";

//Third-Party Libraries
import deleteIcon from "../../assets/icons/deleteIcon.png";
import { Search, Edit, Trash2, Plus } from "lucide-react";

// Components
import CustomButton from "../../components/Buttons/CustomButton";

// Custom Hooks & Utilities
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";
import { roles } from "../../constants/commonConstants";
import { getRemixerUserById } from "../../services/thunks/remixerUserThunks";

// Redux Actions/Thunks
import { deleteTrack, getAllTracks } from "../../services/thunks/TrackThunks";
import { deleteFromCloudflare } from "../../services/thunks/cloudFlareThunks";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";

const MusicTrackTable = () => {
  const auth = useSelector((state) => state.auth);
  const { role } = auth;
  const isRemixer = role === roles.REMIXER;

  const navigate = useNavigate();

  // Custom Hooks
  const [doGetAllTracks, isLoadingTracks] = useThunk(getAllTracks);
  const [doDeleteTrack, isDeletingTrack] = useThunk(deleteTrack);
  const [doDeleteFromCloudflare, isDeletingFromCloudflare] =
    useThunk(deleteFromCloudflare);
  const [doGetRemixerUserById] = useThunk(getRemixerUserById);

  // Local States and Refs
  const [tracks, setTracks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTracks, setTotalTracks] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [trackToDelete, setTrackToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [currentRemixer, setCurrentRemixer] = useState({});

  const tableRemoveRowRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const previousSearchRef = useRef("");

  const itemsPerPage = 13;

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Determine if search term changed
    const searchChanged = searchTerm !== previousSearchRef.current;
    previousSearchRef.current = searchTerm;

    
    const delay = searchChanged && searchTerm ? 500 : 0;

    searchTimeoutRef.current = setTimeout(() => {
     if (searchChanged && searchTerm && currentPage !== 1) {
        setCurrentPage(1);
        fetchTracks(1, searchTerm);
      } else {
        fetchTracks(currentPage, searchTerm);
      }
    }, delay);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, currentPage]);

  const fetchTracks = async (page = 1, search = "") => {
    const response = await doGetAllTracks({
      token: auth.token,
      page,
      limit: itemsPerPage,
      search: search.trim(),
    });

    if (response.success === true) {
      setTracks(response.response.tracks || []);
      setTotalTracks(response.response.pagination?.totalTracks || 0);
    } else {
      setTracks([]);
      setTotalTracks(0);
    }
  };

  const handleDelete = (track) => {
    setTrackToDelete(track);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const promises = [];

      const result = await doDeleteTrack({
        token: auth.token,
        id: trackToDelete._id,
      });

      if (result.success) {
        if (trackToDelete.coverArt?.key) {
          promises.push(
            await doDeleteFromCloudflare({
              key: trackToDelete.coverArt.key,
              type: "images",
            })
          );
        }

        if (Array.isArray(trackToDelete.audioFiles)) {
          for (const audio of trackToDelete.audioFiles) {
            if (audio?.key) {
              promises.push(
                await doDeleteFromCloudflare({ key: audio.key, type: "tracks" })
              );
            }
          }
        }
        await Promise.all(promises);
        
        setShowDeleteModal(false);
        setTrackToDelete(null);
        setShowSuccessMessage(true);
        showToast("success", "Track was deleted successfully.");
        
        // Refresh the current page
        fetchTracks(currentPage, searchTerm);
      } else {
        showToast("error", "Backend deletion failed.");
      }
    } catch (error) {
      showToast("error", "An error occurred during deletion.");
    }
  };

  const handlePaginationChange = (page) => {
    setCurrentPage(page);
  };

  const formatTrackName = (track) => {
    const editTitles =
      track.editTitles?.map((e) => e.editTitle).filter(Boolean) || [];
    const firstEdit = editTitles[0];
    const displayText = track.trackTitle + (firstEdit ? ` (${firstEdit})` : "");
    const tooltipText = displayText;

    return (
      <span title={tooltipText} className="line-clamp-1">
        {displayText}
      </span>
    );
  };

  const formatArtists = (artists) => {
    if (!Array.isArray(artists)) return "";
    const artistsText = artists
      .map((artist) => artist?.artistName || "")
      .join(", ");

    return (
      <span title={artistsText} className="line-clamp-1">
        {artistsText}
      </span>
    );
  };

  useEffect(() => {
    const fetchCurrentRemixerUser = async () => {
      if (!isRemixer || !auth.user?._id) return;

      const res = await doGetRemixerUserById({
        token: auth.token,
        id: auth.user._id,
      });
      if (!res?.success) {
        return;
      }

      setCurrentRemixer(res?.response.data);
    };

    fetchCurrentRemixerUser();
  }, [auth.token, auth.user?._id, isRemixer]);

  const isCurrentRemixerOfTrack = (track) => {
    if (!track?.editTitles || !Array.isArray(track.editTitles)) return false;
    return track.editTitles.some(
      (edit) => edit?.remixer?._id === currentRemixer?.remixerProfile
    );
  };

  return (
    <div className="min-h-screen text-white p-6">
      <div className="flex justify-between items-center mb-6 h-[45px]">
        <div className="relative flex-1 border-lg border-[#808080] pr-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by title, main artist, or ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#45444433] border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-[#808080] focus:outline-none focus:border-cyan-500"
          />
        </div>
        <CustomButton
          useGradientBorder
          rounded="rounded-lg"
          textSize="text-[18px] leading-[24px]"
          padding="py-[10.5px] px-[13.5px]"
          font="font-public-sans"
          border="border-lg"
          type="button"
          width="w-[178px]"
          onClick={() =>
            navigate(isRemixer ? "/remixer-add-tracks" : "/add-tracks")
          }
        >
          Add New Track
        </CustomButton>
      </div>

      <div className="bg-gray-800 rounded-[8px] overflow-x-auto border-[1px] border-[#dde1e6]">
        <table className="w-full min-w-[800px] border-collapse">
          <thead>
            <tr className="bg-[#0D141B]">
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF] w-[15%]">
                ID
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF] w-[35%]">
                Name
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF] w-[30%]">
                Artist(s)
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF] w-[20%] min-w-[250px]">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {
              tracks.map((track, index) => (
                <tr
                  key={track._id}
                  className={`border-b border-gray-700 ${
                    index % 2 === 0 ? "bg-[#1E1E1E]" : "bg-[#191919]"
                  } hover:bg-gray-750`}
                >
                  <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                    <span title={track.trackId} className="block truncate">
                      {track.trackId}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                    {formatTrackName(track)}
                  </td>
                  <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                    {formatArtists(track.artist)}
                  </td>

                  <td className="py-4 px-6 w-[20%] min-w-[200px]">
                    <div className="flex gap-2 flex-nowrap">
                      <button
                        className="flex items-center justify-center gap-1 px-3 py-1 border border-[#B4C007] text-[#B4C007] rounded-[15px] text-sm flex-shrink-0 hover:bg-[#333300] transition"
                        onClick={() =>
                          navigate(
                            isRemixer
                              ? `/remixer-add-tracks/${track._id}`
                              : `/add-tracks/${track._id}`
                          )
                        }
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </button>
                      {!isRemixer && (
                        <button
                          onClick={() => handleDelete(track)}
                          className="flex items-center justify-center gap-1 px-3 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-sm flex-shrink-0 hover:bg-[#330000] transition"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>

                  <hr />
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "16px",
        }}
      >
        <Pagination
          current={currentPage}
          pageSize={itemsPerPage}
          total={totalTracks}
          onChange={handlePaginationChange}
          showSizeChanger={false}
          className="custom-pagination"
        />
      </div>

      {showDeleteModal && (
        <ConfirmDeleteModal
          visible={showDeleteModal}
          onConfirm={async () => {
            await confirmDelete();
          }}
          onCancel={() => {
            setShowDeleteModal(false);
            setTrackToDelete(null);
          }}
          title="Confirm Deletion"
          content={
            "Are you sure you want to delete this track and all its Edits?"
          }
          disabled={isDeletingTrack || isDeletingFromCloudflare}
          loading={isDeletingTrack || isDeletingFromCloudflare}
        />
      )}
    </div>
  );
};

export default MusicTrackTable;
